/**
 * 刷新前的清理序列。版本刷新与 chunk 失败兜底共用同一套，保证两条路径行为一致。
 *
 * 顺序是有意义的：
 * 1. 先停语音。`speechSynthesis` 是**浏览器级服务**，`location.reload()` 不会取消正在朗读的
 *    utterance——旧页面会继续念完并与新页面的播报叠播。这是本功能最容易踩的坑。
 * 2. 再停报警音/关弹窗。
 * 3. 最后再 cancel 一次语音：清理弹窗期间可能又有 utterance 入队。
 *
 * 整个清理过程不允许抛错中断刷新——页面已经要 reload 了，清理失败不构成取消刷新的理由。
 */

import { Modal, notification } from 'ant-design-vue';

import { stopTextToSpeech } from '/@/hooks/nurse/useSpeech';
import { pauseAllAudioPlayers } from '/@/hooks/nurse/useAudioPlayer';
import { clearAllBlockers } from './refreshGuard';

const LOG = '[auto-refresh]';

/**
 * @param force 强制路径（cap 兜底 / chunk 救场）为 true：清理后清空阻塞登记表。
 *   必须先清理再清表，才能维持"表已清空 ⇒ 已无 HARD 状态存在"这个不变量。
 */
export function prepareForReload(force = false): void {
  try {
    stopTextToSpeech();
    pauseAllAudioPlayers();
    // 关掉所有可见弹窗/通知。与 src/router/guard/index.ts 的 createMessageGuard 同一做法。
    // 告警弹窗是 destroyOnClose，销毁时其中的 setTimeout 也一并被清掉。
    Modal.destroyAll();
    notification.destroy();
    stopTextToSpeech();
  } catch (error) {
    console.warn(`${LOG} 刷新前清理失败（不影响刷新）`, error);
  }

  if (force) {
    clearAllBlockers();
  }
}

/**
 * 执行刷新。
 *
 * 用 `location.reload()` 就够了，不需要 cache-busting、也不需要手工保存状态：
 * - hash 路由（createWebHashHistory）→ 完整 URL 含 `#/path` 原样保留；
 * - token 在 localStorage，Persistent 有 beforeunload 落盘 → 免登录；
 * - nginx 对 `location /` 是 no-cache → 一定回源拿到新的 index.html 与新 hash 资源；
 * - 加 `?t=` 之类只会污染 URL 并破坏 hash 路由的 query 语义。
 *
 * 刻意不 try/catch 包裹：reload 失败就让它抛出来，页面停在可观察的状态，
 * 而不是被一个空 catch 吞掉后失去 HARD 保护却在继续跑。
 */
export function reloadNow(): void {
  window.location.reload();
}
